import type { TFunction } from "i18next"
import React, { createContext, useContext, useState } from "react"
import { useNavigate } from "react-router-dom"

import { login } from "@domain/auth"
import useMe from "@hooks/use_me"
import useTranslate from "@hooks/use_translate"
import { LoginFormData } from "@pages/public/login/sub_components/login_form"
import toString from "@utils/functions/to_string"
import i18next from "@utils/localization"
import { showErrorToast } from "@utils/toast"

type LoginPageContextProps = {
  onSubmit: (data: LoginFormData) => Promise<void>
  loading: boolean
  translate: TFunction<"translation", undefined>
}

const defaultValues: LoginPageContextProps = {
  onSubmit: async () => {},
  loading: false,
  translate: i18next.t
}

const LoginPageContext = createContext(defaultValues)

export function LoginPageContextProvider(props: {
  children: React.ReactNode | React.ReactNode[]
}) {
  const { translate } = useTranslate()
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const { setMe } = useMe()

  async function onSubmit(data: LoginFormData) {
    setLoading(true)
    const loginResponse = await login(data)
    setLoading(false)

    if (!loginResponse.isSuccess || !loginResponse.data?.token) {
      const hasErrorMessage = loginResponse.errorMessage
      const errorMessage = hasErrorMessage
        ? toString(loginResponse?.errorMessage)
        : translate("customer_login_error_message")

      showErrorToast(errorMessage)
      return
    }
    const token = loginResponse.data.token
    const user = loginResponse.data.user

    const hasToken = token
    if (hasToken) {
      localStorage.setItem("authToken", token)
    }
    setMe?.(user)

    const params = new URLSearchParams(location.search)
    const redirectUrl = params.get("redirectUrl")

    navigate(redirectUrl || "/dashboard", {})
  }
  return (
    <LoginPageContext.Provider
      value={{
        ...defaultValues,
        onSubmit,
        loading,
        translate
      }}
    >
      {props.children}
    </LoginPageContext.Provider>
  )
}

export function useLoginPageContext() {
  const context = useContext(LoginPageContext)
  if (context === undefined) {
    throw new Error("LoginPage Context Error")
  }
  return context
}
